/* ============================================================
   AddBrandFlow — the dedicated "Add a brand" wizard (multi-brand).
   Ported from the 02-add-brand pack (.abf-* classes). Two input steps
   + a launch state; NOT onboarding, NOT the assistant chat.

   Flow: Basics (name + website→scan, or manual) → Essentials (prefilled
   from scan or empty) → Create → the new brand is created & becomes
   active, research + first content plan kick off in the background →
   launch state. At-limit shows an upsell instead of the form.
   ============================================================ */
import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { useBrand } from '../contexts/BrandContext';
import { rasterizeSvgToPng } from '../lib/rasterize.js';
import { writeChainMarker, readChainMarker } from './AddBrandChainBridge.jsx';
import '../styles/add-brand.css';

function unwrap(res) {
  return res && res.data && typeof res.data === 'object' ? res.data : res;
}
function stripEmpty(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));
}
function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

// Inline glyphs (the pack uses inline SVGs).
const Ic = {
  tag: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>,
  globe: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>,
  search: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m21 21-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z" /></svg>,
  sparkle: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l2.5 2.5M16.5 16.5 19 19M5 19l2.5-2.5M16.5 7.5 19 5" /></svg>,
  arrow: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  chev: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>,
  info: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>,
  check: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6 9 17l-5-5" /></svg>,
  cal: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></svg>,
  draft: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="m8 12 3 3 5-6" /></svg>,
  lock: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>,
  plus: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 5v14M5 12h14" /></svg>,
};

export default function AddBrandFlow({ onClose }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const brand = useBrand();

  const [step, setStep] = useState(brand.atLimit ? 'atlimit' : 'basics'); // basics | essentials | launching | atlimit
  // TASK-331: distinguishes the 'atlimit' card's copy — a lapsed org (0 brand
  // slots, gateway-blocked PLAN_LAPSED before this ever reaches a quota check)
  // needs "Renew", not "Upgrade your plan to run more brands".
  const [limitReason, setLimitReason] = useState('quota'); // 'quota' | 'lapsed'
  const [manual, setManual] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [desc, setDesc] = useState('');
  const [audience, setAudience] = useState('');
  const [offer, setOffer] = useState('');
  const [positioning, setPositioning] = useState('');
  const [colors, setColors] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [scanned, setScanned] = useState(false);
  // Hidden native colour input — the "+" swatch opens the OS picker.
  const colorInputRef = useRef(null);
  // Launch pipeline DISPLAY state. The chain itself is driven app-wide by
  // AddBrandChainBridge via a localStorage marker (so it survives leaving this
  // modal, navigation, even reloads) — this modal only mirrors the marker.
  const [pipe, setPipe] = useState({ research: 'running', plan: 'queued' });
  const createdIdRef = useRef(null);

  const close = useCallback(() => { onClose && onClose(); }, [onClose]);

  // Escape closes (except mid-launch, where the brand is already created).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && step !== 'launching') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, close]);

  // Mirror the chain marker (owned by AddBrandChainBridge) into the pipeline
  // rows. Cheap localStorage read — no network from the modal.
  React.useEffect(() => {
    if (step !== 'launching') return undefined;
    const map = () => {
      const m = readChainMarker();
      if (!m || m.brandId !== (createdIdRef.current || m.brandId)) return;
      if (m.stage === 'plan') setPipe({ research: 'done', plan: 'running' });
      else if (m.stage === 'done') setPipe({ research: 'done', plan: 'done' });
      else if (m.stage === 'failed') {
        setPipe(m.failedStage === 'research'
          ? { research: 'failed', plan: 'queued' }
          : { research: 'done', plan: 'failed' });
      } else setPipe({ research: 'running', plan: 'queued' });
    };
    map();
    const id = setInterval(map, 2000);
    return () => clearInterval(id);
  }, [step]);

  const monogram = (name || 'B').trim().charAt(0).toUpperCase();

  // Runs the SAME two-scan the onboarding "we scanned your site" card uses:
  // analyzeWebsite → suggestedProfilePatch (business fields) + detectBrand →
  // logo(dataUrl)/colors, in parallel. Either failing is fine → manual entry.
  const runScan = useCallback(async () => {
    const url = website.trim();
    if (!url) { setStep('essentials'); return; }
    setErr('');
    setScanning(true);
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    const [ws, bd] = await Promise.all([
      api.onboarding.analyzeWebsite({ url: normalized }).then(unwrap).catch(() => null),
      api.onboarding.detectBrand({ url: normalized, brandName: name.trim() || undefined }).then(unwrap).catch(() => null),
    ]);
    // Business profile (same fields onboarding prefills).
    const patch = ws?.suggestedProfilePatch || {};
    if (patch.businessDescription) setDesc(String(patch.businessDescription));
    if (patch.targetAudience) setAudience(String(patch.targetAudience));
    if (patch.offer) setOffer(String(patch.offer));
    if (patch.positioning) setPositioning(String(patch.positioning));
    // Logo + colours. Logo is a dataUrl; SVG (the most common real-logo format)
    // is rasterized to PNG client-side because the server rejects raw SVG.
    if (bd?.found) {
      let logo = bd.logo?.dataUrl || null;
      if (logo && String(bd.logo?.mimeType || '').includes('svg')) {
        try {
          const svgBlob = await (await fetch(logo)).blob();
          logo = await blobToDataUrl(await rasterizeSvgToPng(svgBlob));
        } catch { logo = null; }
      }
      if (logo) setLogoUrl(logo);
      if (Array.isArray(bd.colors)) setColors(bd.colors.filter(Boolean).slice(0, 6));
    }
    setScanned(true);
    setScanning(false);
    setStep('essentials');
  }, [website, name]);

  const create = useCallback(async () => {
    if (!name.trim()) return;
    setErr('');
    setBusy(true);
    try {
      const created = unwrap(await api.brands.create({
        name: name.trim(),
        website: website.trim() || undefined,
        description: desc.trim() || undefined,
      }));
      if (!created?.brand?.id) {
        // Fall back to the brand list refresh if the shape is unexpected.
        await brand.refresh();
      } else {
        // BrandContext.addBrand also does this, but api.brands.create was called
        // directly here; mirror the active-brand set so scoping is correct.
        createdIdRef.current = created.brand.id;
        api.setActiveBrandId(created.brand.id);
        await brand.refresh();
      }

      // Persist the essentials onto the (now active) new brand — best-effort:
      // PUT is a full-replace requiring businessDescription+targetAudience, so a
      // thin manual entry may 400; the brand + research still proceed.
      try {
        await api.profile.updateBrandProfile(stripEmpty({
          businessName: name.trim(),
          businessDescription: desc.trim(),
          targetAudience: audience.trim(),
          offer: offer.trim(),
          positioning: positioning.trim(),
          brandColors: colors.length ? colors : undefined,
          websiteUrl: website.trim() ? (website.trim().startsWith('http') ? website.trim() : `https://${website.trim()}`) : undefined,
        }));
      } catch (_) { /* essentials save is best-effort */ }

      // Persist the scanned/kept logo onto the (now active) new brand — same
      // path onboarding uses (R2 + media_assets + sets logoAssetId).
      if (logoUrl) {
        try { await api.onboarding.addReference({ type: 'logo', fileName: 'brand-logo.png', dataUrl: logoUrl }); } catch (_) { /* logo persist best-effort */ }
      }

      // Kick market research for the new active brand (brandProfileId is
      // auto-attached by the api client). Content plan follows research.
      const intake = stripEmpty({
        brandName: name.trim(),
        business: desc.trim(),
        businessDescription: desc.trim(),
        targetAudience: audience.trim(),
        offer: offer.trim(),
        positioning: positioning.trim(),
        website: website.trim() || undefined,
        language: i18n.language,
      });
      try { await api.research.create({ trigger: 'manual', language: i18n.language, intake }); } catch (_) { /* watch page will surface errors */ }
      // Hand the chain to AddBrandChainBridge (app-wide): research ready →
      // plan build with the explicit credit confirm. The marker survives
      // closing this modal, navigation and reloads.
      if (createdIdRef.current) {
        writeChainMarker({ brandId: createdIdRef.current, name: name.trim(), stage: 'research', ts: Date.now() });
      }

      setStep('launching');
    } catch (e) {
      // 402 = at the plan's brand limit (or, for a lapsed org, PLAN_LAPSED —
      // check that FIRST since it also arrives as a plain 402).
      const lapsed = e?.code === 'PLAN_LAPSED';
      const code = e?.status || e?.code || e?.details?.code;
      if (lapsed || code === 402 || e?.code === 'QUOTA_EXCEEDED') {
        setLimitReason(lapsed ? 'lapsed' : 'quota');
        setStep('atlimit');
      } else { setErr(t('addBrand.createError', { defaultValue: 'Could not create the brand. Please try again.' })); }
    } finally {
      setBusy(false);
    }
  }, [name, website, desc, audience, offer, positioning, brand, i18n.language, t]);

  const goDashboard = () => { close(); navigate('/app'); };
  const goResearch = () => { close(); navigate('/app/research'); };
  const goPlans = () => { close(); navigate('/app/plans'); };

  const tr = (k, d) => t(k, { defaultValue: d });

  let card;
  if (step === 'atlimit') {
    const lapsed = limitReason === 'lapsed';
    card = (
      <div className="abf-card">
        <div className="abf-limit">
          <div className="lm">{Ic.lock}</div>
          <h2>{lapsed ? tr('lapsed.bannerTitle', 'Your plan has lapsed') : tr('addBrand.limitTitle', 'You’re using all your brands')}</h2>
          <p>{lapsed ? tr('lapsed.bannerBody', 'Everything you have created is still here to read. Creating, generating and publishing are paused until you renew.') : tr('addBrand.limitBody', 'Upgrade your plan to run more brands from one account.')}</p>
          <div className="abf-limitbrands">
            {brand.brands.slice(0, 3).map((b) => (
              <div className="abf-bpv" key={b.id}>
                <span className="abf-btile">{b.monogram || 'B'}</span>
                <span className="n">{b.name}</span>
              </div>
            ))}
          </div>
          <button type="button" className="abf-btn-up" onClick={goPlans}>{Ic.sparkle}{lapsed ? tr('lapsed.renew', 'Renew your plan') : tr('addBrand.seePlans', 'See plans')}</button>
          <div className="abf-launchfoot" style={{ marginTop: 12 }}>
            <button type="button" className="abf-btn-gh" onClick={close}>{tr('common.close', 'Close')}</button>
          </div>
        </div>
      </div>
    );
  } else if (step === 'launching') {
    card = (
      <div className="abf-card">
        <div className="abf-launch">
          <div className="abf-mark"><span className="abf-ring" />{logoUrl ? <img src={logoUrl} alt="" /> : Ic.sparkle}</div>
          <h2>{t('addBrand.launchTitle', { name: name.trim(), defaultValue: 'Setting up {{name}}' })}</h2>
          <p>{tr('addBrand.launchBody', 'Running market research and building your first content plan — this takes a few minutes.')}</p>
          <div className="abf-pipe">
            {(() => {
              const stepCls = (s) => (s === 'running' ? 'run' : s === 'done' ? 'done' : s === 'failed' ? 'fail' : 'wait');
              const stepIc = (s, idle) => (s === 'running' ? <span className="abf-spin" /> : s === 'done' ? Ic.check : idle);
              const stepPs = (s) => (s === 'running' ? tr('addBrand.pipeRunning', 'Running')
                : s === 'done' ? tr('addBrand.pipeDone', 'Done')
                : s === 'failed' ? tr('addBrand.pipeFailed', 'Failed')
                : tr('addBrand.pipeQueued', 'Queued'));
              const postState = pipe.plan === 'done' ? 'ready' : 'queued';
              return (
                <>
                  <div className={`abf-pstep ${stepCls(pipe.research)}`}><span className="pi">{stepIc(pipe.research, <span className="abf-spin" />)}</span><span className="pt">{tr('addBrand.pipeResearch', 'Market research')}</span><span className="ps">{stepPs(pipe.research)}</span></div>
                  <div className={`abf-pstep ${stepCls(pipe.plan)}`}><span className="pi">{stepIc(pipe.plan, Ic.cal)}</span><span className="pt">{tr('addBrand.pipePlan', 'First content plan')}</span><span className="ps">{stepPs(pipe.plan)}</span></div>
                  <div className={`abf-pstep ${postState === 'ready' ? 'done' : 'wait'}`}><span className="pi">{Ic.draft}</span><span className="pt">{tr('addBrand.pipePost', 'First post draft')}</span><span className="ps">{postState === 'ready' ? tr('addBrand.pipeReady', 'Ready when you are') : tr('addBrand.pipeQueued', 'Queued')}</span></div>
                </>
              );
            })()}
          </div>
          <div className="abf-activepill"><span className="abf-btile">{logoUrl ? <img src={logoUrl} alt="" /> : monogram}</span>{t('addBrand.nowActive', { name: name.trim(), defaultValue: '{{name}} is now your active brand' })}</div>
          {/* This is a send-off, not a waiting room — the chain runs app-wide
              (AddBrandChainBridge), so leaving is explicitly safe. */}
          <p className="abf-leave">{tr('addBrand.launchLeave', 'You can leave — this keeps running in the background.')}</p>
          <div className="abf-launchfoot">
            <button type="button" className="abf-btn-gh" onClick={goResearch}>{tr('addBrand.watchProgress', 'Watch progress')}</button>
            {pipe.plan === 'done'
              ? <button type="button" className="abf-btn-pr" onClick={() => { close(); navigate('/app/create'); }}>{tr('addBrand.createFirstPost', 'Create your first post')}{Ic.arrow}</button>
              : <button type="button" className="abf-btn-pr" onClick={goDashboard}>{tr('addBrand.goDashboard', 'Go to dashboard')}{Ic.arrow}</button>}
          </div>
        </div>
      </div>
    );
  } else if (step === 'basics') {
    card = (
      <div className="abf-card">
        <div className="abf-head">
          <div className="abf-rail">
            <div className="abf-srow on"><span className="abf-snum">1</span><span className="abf-slab">{tr('addBrand.stepBasics', 'Basics')}</span><span className="abf-sline" /></div>
            <div className="abf-srow"><span className="abf-snum">2</span><span className="abf-slab">{tr('addBrand.stepEssentials', 'Essentials')}</span></div>
          </div>
          <h1 className="abf-title">{tr('addBrand.startTitle', 'Add a brand')}</h1>
          <p className="abf-sub">{tr('addBrand.startSub', 'Set up a new brand in your workspace.')}</p>
        </div>
        <div className="abf-body">
          <div className="abf-fld">
            <label>{tr('addBrand.nameLabel', 'Brand name')}</label>
            <div className={`abf-inp ${name ? 'filled' : ''}`}>
              <span className="lead" style={{ display: 'inline-flex' }}>{Ic.tag}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} aria-label={tr('addBrand.nameLabel', 'Brand name')} autoFocus />
            </div>
          </div>
          {!manual && (
            <div className="abf-fld">
              <label>{tr('addBrand.websiteLabel', 'Website')} <span className="abf-opt">{tr('common.optional', 'optional')}</span></label>
              <div className={`abf-inp ${scanning ? 'working' : ''}`}>
                <span className="lead" style={{ display: 'inline-flex' }}>{Ic.globe}</span>
                {scanning
                  ? <span className="abf-wt">{tr('addBrand.scanning', 'Reading')} <b>{website.trim()}</b>…</span>
                  : <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="verde-cafe.com" aria-label={tr('addBrand.websiteLabel', 'Website')} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) runScan(); }} />}
                {scanning && <span className="abf-spin" />}
              </div>
              <div className="abf-hint">{Ic.sparkle}{tr('addBrand.scanHint', 'We’ll pull the logo, colours and what the business does from your site.')}</div>
            </div>
          )}
          {!manual && (
            <button type="button" className="abf-manual" onClick={() => setManual(true)}>{tr('addBrand.manualLink', 'No website? Describe it manually')}{Ic.chev}</button>
          )}
          {err && <div className="abf-err" role="alert">{err}</div>}
        </div>
        <div className="abf-foot">
          <button type="button" className="abf-btn-gh" onClick={close}>{tr('common.cancel', 'Cancel')}</button>
          <button
            type="button"
            className="abf-btn-pr"
            onClick={() => { if (!manual && website.trim()) runScan(); else setStep('essentials'); }}
            disabled={!name.trim() || scanning}
          >
            {scanning
              ? <><span className="abf-spin" />{tr('addBrand.scanning', 'Reading')}…</>
              : <>{tr('common.continue', 'Continue')}{Ic.arrow}</>}
          </button>
        </div>
      </div>
    );
  } else {
    // essentials
    card = (
      <div className="abf-card">
        <div className="abf-head">
          <div className="abf-rail">
            <div className="abf-srow done"><span className="abf-snum">{Ic.check}</span><span className="abf-slab">{tr('addBrand.stepBasics', 'Basics')}</span><span className="abf-sline" /></div>
            <div className="abf-srow on"><span className="abf-snum">2</span><span className="abf-slab">{tr('addBrand.stepEssentials', 'Essentials')}</span></div>
          </div>
          <h1 className="abf-title">{tr('addBrand.essentialsTitle', 'A few essentials')}</h1>
          <p className="abf-sub">{scanned ? tr('addBrand.essentialsSubScanned', 'Pulled from your site — edit anything that’s off.') : tr('addBrand.essentialsSubManual', 'Tell us the basics so the research is on target.')}</p>
        </div>
        <div className="abf-body">
          <div className="abf-look">
            <span className="abf-btile">{logoUrl ? <img src={logoUrl} alt="" /> : monogram}</span>
            <div className="abf-swrap">
              <div className="abf-sk">{scanned ? tr('addBrand.brandLookDetected', 'Brand look · detected') : tr('addBrand.brandLook', 'Brand look')}</div>
              <div className="abf-sw">
                {colors.map((c, i) => (
                  <span key={`${c}-${i}`} className="abf-chip" style={{ background: c }} title={c}>
                    <button
                      type="button"
                      className="x"
                      aria-label={tr('addBrand.removeColour', 'Remove colour')}
                      onClick={() => setColors((cs) => cs.filter((_, j) => j !== i))}
                    >✕</button>
                  </span>
                ))}
                <button
                  type="button"
                  className="abf-chipadd"
                  aria-label={tr('addBrand.addColour', 'Add colour')}
                  onClick={() => colorInputRef.current?.click()}
                >{Ic.plus}</button>
                <input
                  ref={colorInputRef}
                  type="color"
                  className="abf-colorinput"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(e) => {
                    const hex = String(e.target.value || '').toUpperCase();
                    if (hex) setColors((cs) => (cs.includes(hex) ? cs : [...cs, hex]).slice(0, 8));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="abf-fld"><label>{tr('addBrand.fWhat', 'What the business does')}</label><div className={`abf-inp ta ${desc ? 'filled' : ''}`}><textarea rows="2" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={tr('addBrand.fWhatPh', 'A short description of the business…')} aria-label={tr('addBrand.fWhat', 'What the business does')} /></div></div>
          <div className="abf-fld"><label>{tr('addBrand.fAudience', 'Target audience')}</label><div className={`abf-inp ${audience ? 'filled' : ''}`}><input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder={tr('addBrand.fAudiencePh', 'Who is it for?')} aria-label={tr('addBrand.fAudience', 'Target audience')} /></div></div>
          <div className="abf-fld"><label>{tr('addBrand.fOffer', 'Main offer or product')}</label><div className={`abf-inp ${offer ? 'filled' : ''}`}><input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder={tr('addBrand.fOfferPh', 'What do they sell?')} aria-label={tr('addBrand.fOffer', 'Main offer or product')} /></div></div>
          <div className="abf-fld"><label>{tr('addBrand.fDiff', 'What makes it different')} <span className="abf-opt">{tr('common.optional', 'optional')}</span></label><div className={`abf-inp ${positioning ? 'filled' : ''}`}><input value={positioning} onChange={(e) => setPositioning(e.target.value)} placeholder={tr('addBrand.fDiffPh', 'Positioning, edge, story…')} aria-label={tr('addBrand.fDiff', 'What makes it different')} /></div></div>
          <div className="abf-note">{Ic.info}{tr('addBrand.refineNote', 'You can refine everything later in Brand Identity.')}</div>
          {err && <div className="abf-err" role="alert">{err}</div>}
        </div>
        <div className="abf-foot">
          <button type="button" className="abf-btn-gh" onClick={() => setStep('basics')} disabled={busy}>{tr('common.back', 'Back')}</button>
          <button type="button" className="abf-btn-pr" onClick={create} disabled={busy || !name.trim()}>{busy ? <span className="abf-spin" /> : Ic.sparkle}{tr('addBrand.createCta', 'Create brand & start research')}</button>
        </div>
      </div>
    );
  }

  return createPortal(
    <div className="abf-scene" role="dialog" aria-modal="true" aria-label={tr('addBrand.startTitle', 'Add a brand')}>
      {card}
    </div>,
    document.body
  );
}
